// src/context/AuthContext.jsx

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  getCurrentUser,
  login as apiLogin,
  logout as apiLogout,
  isAuthenticated as checkIsAuthenticated,
} from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = () => {
    try {
      const currentUser = getCurrentUser();
      const authenticated = checkIsAuthenticated();

      if (authenticated && currentUser) {
        setUser(currentUser);
      } else {
        setUser(null);
      }
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (userId, password) => {
    try {
      setLoading(true);
      const user = await apiLogin(userId, password);
      setUser(user);
      return user;
    } catch (error) {
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await apiLogout();
    } finally {
      setUser(null);
      setLoading(false);
    }
  };

  const refreshUser = () => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser,
      isAdminOrHR: user?.role === 'admin' || user?.role === 'hr_manager',
      isEmployee: user?.role === 'employee',
      isHR: user?.role === 'admin' || user?.role === 'hr_manager',
    }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};